import React, { useCallback, useEffect, useState } from 'react';
import { BioLinkAutoAddResult } from 'fym-shared/src/smartlinks/bioLinkSettings';
import { TypedMemo } from '../../../common/types';
import { LoadingView } from '../../../common/views/LoadingView';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { useAddSongArtist } from '../../../onboarding/addSong/useAddSongArtist';
import { useAddSongRoute } from './useAddSongRoute';
import { AddSongPage } from './AddSongPage';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole page; each part uses its own slice of it */
import styles from './CatalogAddSong.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CatalogAddSongViewProps {
  /** `onboarding` is "Add my song" (`/app/addSong`): the same steps, its own Done (design f4). */
  context?: 'catalog' | 'onboarding';
}

/**
 * The routes `/app/catalog/new` and `/app/addSong` (design catalog-add-song a·why): a full page
 * with no sidebar. With the demo artist active, the page first selects the user's own artist (the
 * demo is read-only). A workspace with no artist goes to the welcome page, which adds one.
 */
const CatalogAddSongViewComponent: React.FC<CatalogAddSongViewProps> = ({ context = 'catalog' }) => {
  const router = useSMLRouter();
  const route = useAddSongRoute(context);
  const { accountId, artistName, hasNoArtist } = useAddSongArtist();
  // The Bio Link answer of each saved song, for its Done row (design smartlink-editor e2).
  const [bioLinkResults, setBioLinkResults] = useState<Record<string, BioLinkAutoAddResult>>({});
  const onBioLinkResult = useCallback(
    (itemUuid: string, result: BioLinkAutoAddResult) =>
      setBioLinkResults((current) => ({ ...current, [itemUuid]: result })),
    []
  );

  useEffect(() => {
    if (hasNoArtist) {
      router.replace(SMLRoutes.welcome);
    }
  }, [hasNoArtist, router]);

  if (accountId === null) {
    return (
      <div className={styles.page}>
        <LoadingView />
      </div>
    );
  }
  // A new song (no row in the address) starts with fresh state; an artist switch too.
  return (
    <AddSongPage
      key={`${accountId}:${route.itemUuid ?? 'new'}`}
      accountId={accountId}
      artistName={artistName}
      route={route}
      bioLinkResult={route.itemUuid === undefined ? undefined : bioLinkResults[route.itemUuid]}
      onBioLinkResult={onBioLinkResult}
    />
  );
};

export const CatalogAddSongView = TypedMemo(CatalogAddSongViewComponent);
